import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import React, { useEffect, useState, ReactElement, memo } from "react";
import { isIOSPlatform } from "util/methods";
import ConfirmationDialog from "./ConfirmationDialog";

export interface PopoverAction {
  name: string;
  callback: () => Promise<void> | void;
  shouldShowConfirmation?: boolean;
  confirmationText?: string;
  confirmationSubmitText?: string;
}

interface FuzeyPopoverProps {
  triggerIcon?: ReactElement;
  triggerSize?: "lg" | "md" | "sm" | "xs";
  dropShadow?: boolean;
  actions: PopoverAction[];
}

const FuzeyPopover = memo(
  ({
    triggerIcon,
    actions,
    triggerSize,
    dropShadow = false,
  }: FuzeyPopoverProps) => {
    if (actions.length === 0) {
      return null;
    }

    const defaultConfirmationText =
      "Are you sure you want to perform this action?";
    const defaultConfirmationSubmitText = "Yes";
    const [showConfirmationModal, setShowConfirmationModal] =
      useState<boolean>(false);
    const [confirmationCallback, setConfirmationCallback] = useState<
      () => Promise<void> | void
    >(() => () => {});
    const [confirmationText, setConfirmationText] = useState<string>(
      defaultConfirmationText
    );
    const [confirmationSubmitText, setConfirmationSubmitText] =
      useState<string>(defaultConfirmationSubmitText);
    const { isOpen, onToggle, onClose } = useDisclosure();
    const isBaseSize = useBreakpointValue(
      { base: true, md: false },
      { ssr: false }
    );
    const { colorScheme } = useCustomChakraTheme();
    const [touchTimeout, setTouchTimeout] = useState<number | undefined>();
    const [isOverlayShown, setIsOverlayShown] = useState<boolean>(false);

    useEffect(() => {
      const timeout: ReturnType<typeof setTimeout> = setTimeout(() => {
        setIsOverlayShown(isOpen);
      }, 200);
      return () => {
        clearTimeout(timeout);
      };
    }, [isOpen]);

    return (
      <>
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          closeOnBlur={true}
          isLazy={true}
        >
          <PopoverTrigger>
            {isBaseSize && dropShadow && isIOSPlatform() ? (
              <Button
                position="absolute"
                width="100%"
                height="100%"
                bgColor="transparent"
                borderRadius="none"
                variant="unstyled"
                top={0}
                left={0}
                onTouchStart={(e) => {
                  setTouchTimeout(
                    window.setTimeout(() => {
                      e.stopPropagation();
                      onToggle();
                    }, 500)
                  );
                }}
                onClick={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                onTouchEnd={() => {
                  clearTimeout(touchTimeout);
                }}
                onTouchMove={() => clearTimeout(touchTimeout)}
              />
            ) : (
              <IconButton
                aria-label="Dropdown Trigger"
                borderRadius="full"
                icon={triggerIcon || <ChevronDownIcon />}
                variant="ghost"
                colorScheme={colorScheme}
                size={triggerSize || "xs"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              />
            )}
          </PopoverTrigger>
          <Portal>
            {isBaseSize && dropShadow ? (
              <Box
                display={isOverlayShown ? "initial" : "none"}
                position="absolute"
                width="100%"
                height="100%"
                left={0}
                top={0}
                bgColor="blackAlpha.500"
                opacity={1}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                onContextMenu={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
              />
            ) : null}
            <PopoverContent width="fit-content">
              <PopoverArrow />
              <PopoverBody zIndex={999} px={0}>
                {actions.map((a, index) => (
                  <div key={a.name}>
                    <Button
                      colorScheme={colorScheme}
                      px={5}
                      variant="ghost"
                      fontWeight="normal"
                      w="100%"
                      borderRadius="none"
                      justifyContent="start"
                      mx={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (a.shouldShowConfirmation) {
                          setConfirmationCallback(() => {
                            return () => {
                              a.callback();
                              onClose();
                              setConfirmationCallback(() => () => {});
                              setConfirmationText(defaultConfirmationText);
                              setConfirmationSubmitText(
                                defaultConfirmationSubmitText
                              );
                            };
                          });

                          if (a.confirmationText) {
                            setConfirmationText(a.confirmationText);
                          }

                          if (a.confirmationSubmitText) {
                            setConfirmationSubmitText(a.confirmationSubmitText);
                          }

                          setShowConfirmationModal(true);

                          return;
                        }

                        a.callback();
                        onClose();
                      }}
                    >
                      {a.name}
                    </Button>
                    {index === actions.length - 1 ? null : <Divider />}
                  </div>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
        <ConfirmationDialog
          isOpen={showConfirmationModal}
          setIsOpen={setShowConfirmationModal}
          messageText={confirmationText}
          buttonText={confirmationSubmitText}
          confirmationCallback={confirmationCallback}
        />
      </>
    );
  }
);

export default FuzeyPopover;
